/**
 * The options tab's data rules, apart from its screens: the values an
 * option offers, the variant rows its axes span, SKU generation and the
 * checks a save runs. The server applies the same rules when it prices a
 * line (aldoo-na/store/product/extensions/options/hooks.js).
 */

const isRecord = (value) => !!value && typeof value === "object" && !Array.isArray(value)

/** A label as the admin reads it: a plain string, or the map's en / first text. */
export const labelText = (label, code = "en") => {
  if (typeof label === "string") return label
  if (!isRecord(label)) return ""
  return label[code] || label.en || Object.values(label).find((text) => typeof text === "string" && text) || ""
}

/** A value list: records with a key, each key once, in their order. */
export const cleanValues = (values) => {
  if (!Array.isArray(values)) return []
  const seen = new Set()
  return values
    .map((value, index) => ({ value, index }))
    .filter(({ value }) => isRecord(value) && String(value.key ?? "").trim())
    .sort((a, b) => (Number(a.value.order ?? a.index) - Number(b.value.order ?? b.index)) || a.index - b.index)
    .map(({ value }) => ({ ...value, key: String(value.key).trim() }))
    .filter((value) => (seen.has(value.key) ? false : seen.add(value.key)))
}

/** Every value an option can offer: its set's, then its own the set lacks. */
export const candidateValues = (option, sets) => {
  const set = option?.set ? sets?.find((entry) => entry.key === option.set) : null
  const values = [...cleanValues(set?.values)]
  for (const own of cleanValues(option?.values)) if (!values.some((value) => value.key === own.key)) values.push(own)
  return values
}

/** The values the option offers: the candidates narrowed by `allowed` (empty = all). */
export const offeredValues = (option, sets) => {
  const values = candidateValues(option, sets)
  const allowed = Array.isArray(option?.allowed) ? option.allowed.filter(Boolean) : []
  return allowed.length ? values.filter((value) => allowed.includes(value.key)) : values
}

/** The combinations of the axis options' values: [{ size: "M", color: "black" }, ...]. */
export const combinations = (options, sets) => {
  const axes = (options || []).filter((option) => option?.variantAxis && option.key)
  if (!axes.length) return []
  return axes.reduce(
    (rows, axis) => rows.flatMap((row) => offeredValues(axis, sets).map((value) => ({ ...row, [axis.key]: value.key }))),
    [{}]
  )
}

const sameCombination = (a, b, axes) => axes.every((axis) => String(a?.[axis.key] ?? "") === String(b?.[axis.key] ?? ""))

/**
 * The variant rows: one per combination, the stored variant when there is
 * one, else a new one (its SKU generated from `parentSku`). Variants no
 * combination names any more are answered apart, as `orphans`.
 */
export const variantRows = (options, variants, sets, parentSku = "") => {
  const axes = (options || []).filter((option) => option?.variantAxis && option.key)
  const stored = Array.isArray(variants) ? variants.filter(isRecord) : []
  const all = combinations(options, sets)
  const rows = all.map((combination) => {
    const variant = stored.find((entry) => sameCombination(entry.options, combination, axes))
    return variant ? { ...variant, options: combination, isNew: false } : { sku: generateSku(parentSku, combination, axes), ean: "", options: combination, stock: 0, price: null, images: [], enabled: true, isNew: true }
  })
  // a row the grid made and nobody touched goes with its combination
  const pristine = (variant) =>
    !variant.ean && (variant.price === null || variant.price === undefined) && !variant.images?.length && !variant.stock && variant.enabled !== false &&
    variant.sku === generateSku(parentSku, variant.options, Object.keys(variant.options || {}).map((key) => ({ key })))
  const orphans = stored.filter((variant) => !all.some((combination) => sameCombination(variant.options, combination, axes)) && !pristine(variant))
  return { rows, orphans, axes }
}

/** A row as it is stored: what the grid adds (isNew) left out. */
export const asVariant = ({ isNew, ...row }) => row

/** A part of a SKU: the value's key, spaces as dashes, upper case. */
const skuPart = (value) => String(value ?? "").trim().replace(/\s+/g, "-").toUpperCase()

/** `{parent}-{value}[-{value}…]` in the axes' order. */
export const generateSku = (parent, combination, axes) => [String(parent || "").trim(), ...axes.map((axis) => skuPart(combination?.[axis.key]))].filter(Boolean).join("-")

/** What stops a save: duplicate / empty option keys, an axis without values, duplicate or missing SKUs. */
export const problemsOf = (options, rows, sets) => {
  const problems = []
  const keys = (options || []).map((option) => String(option?.key || "").trim())
  if (keys.some((key) => !key)) problems.push("Every option needs a key")
  const duplicate = keys.find((key, index) => key && keys.indexOf(key) !== index)
  if (duplicate) problems.push(`Two options have the key ${duplicate}`)
  for (const option of options || []) {
    if (!option?.key) continue
    if (!option.set && !cleanValues(option.values).length) problems.push(`${option.key}: pick an option set or add values`)
    else if (option.variantAxis && !offeredValues(option, sets).length) problems.push(`${option.key}: no value is allowed`)
  }
  const skus = (rows || []).filter((row) => row.enabled !== false).map((row) => String(row.sku || "").trim())
  if (skus.some((sku) => !sku)) problems.push("Every enabled variant needs a SKU (Generate SKUs fills them)")
  const twice = (rows || []).map((row) => String(row.sku || "").trim()).find((sku, index, all) => sku && all.indexOf(sku) !== index)
  if (twice) problems.push(`Two variants have the SKU ${twice}`)
  // a variant's price is its own or the product's (none): never below 0
  const negative = (rows || []).find((row) => row.price !== null && row.price !== undefined && Number(row.price) < 0)
  if (negative) problems.push(`${negative.sku || "A variant"}: the price cannot be below 0 (clear it to sell at the product's)`)
  return problems
}

// ---------------------------------------------------------------------------
// the option's key, worked out (the admin is never asked for it)
// ---------------------------------------------------------------------------

// what the common sets are about: their options share the storefront's well-known keys
const KNOWN_KEYS = [
  [/size|размер/i, "size"],
  [/colou?r|цвят/i, "color"],
  [/material|материя|материал/i, "material"],
  [/length|дължина/i, "length"],
]
// Cyrillic as Latin (Bulgarian's streamlined system), so a label in Bulgarian still gives a key
const CYRILLIC = { а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ж: "zh", з: "z", и: "i", й: "y", к: "k", л: "l", м: "m", н: "n", о: "o", п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "ts", ч: "ch", ш: "sh", щ: "sht", ъ: "a", ь: "y", ю: "yu", я: "ya" }
const slug = (text) =>
  String(text || "")
    .toLowerCase()
    .replace(/[а-я]/g, (letter) => CYRILLIC[letter] ?? "")
    .replace(/^[^:]*:/, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")

/**
 * The key an option takes when it has none: from what its values are (the set's key and
 * name, else the option's label) — "size", "color", … — else a slug of them, else
 * "option"; made unique among `taken` (another option's key) with a number.
 */
export const deriveKey = (option, sets = [], taken = []) => {
  const set = option?.set ? sets.find((entry) => entry.key === option.set) : null
  const sources = [option?.set, labelText(set?.label), labelText(option?.label, "en"), labelText(option?.label)].filter(Boolean)
  let key = ""
  for (const text of sources) {
    const known = KNOWN_KEYS.find(([pattern]) => pattern.test(text))
    if (known) {
      key = known[1]
      break
    }
  }
  if (!key) key = sources.map(slug).find(Boolean) || "option"
  if (!taken.includes(key)) return key
  let n = 2
  while (taken.includes(`${key}${n}`)) n++
  return `${key}${n}`
}

/**
 * The options, each with a key: one without gets its key worked out, and keeps working it
 * out (`_autoKey`) while it is new — a set or a label picked after it was added names it.
 * A saved option keeps its key (its variants are filed under it). `_autoKey` is not stored.
 */
export const withKeys = (options, sets = []) => {
  const fixed = (options || []).filter((option) => String(option?.key || "").trim() && !option._autoKey).map((option) => String(option.key).trim())
  const taken = [...fixed]
  return (options || []).map((option) => {
    if (String(option?.key || "").trim() && !option._autoKey) return option
    const key = deriveKey(option, sets, taken)
    taken.push(key)
    return { ...option, key, _autoKey: true }
  })
}

